import pdfMake from "pdfmake/build/pdfmake";
import { pdfMake as pdfMakeFonts } from "../../vfs_fonts";
import { useQuoteCalculator } from "./quote-calculator.component";
import { accountTypeCoverageSummaries, accountTypeHeaders, CH_TYPES, generateGroupInsuranceTable, getDescription, getImageBase64, provincesTaxRates } from "./quote-utils";
import { useEffect, useState } from "react";
import { Download } from "lucide-react";
import { authFetch, getApiUrl, SessionExpiredError } from "../../utils/api";
import { PDFDocument } from 'pdf-lib';

pdfMake.vfs = pdfMakeFonts.vfs;

pdfMake.fonts = {
    Merriweather: {
        normal: 'Merriweather-Regular.ttf',
        bold: 'Merriweather-Bold.ttf',
        italics: 'Merriweather-Italic.ttf',
        bolditalics: 'Merriweather-BoldItalic.ttf'
    },
    SourceSansPro: {
        normal: 'SourceSansPro-Regular.otf',
        bold: 'SourceSansPro-Bold.otf',
        italics: 'SourceSansPro-Italic.otf',
        bolditalics: 'SourceSansPro-BoldItalic.otf'
    }
};

const PDF_STYLES = {
    fontSize: {
        title: 22,
        subtitle: 16,
        sectionHeader: 13,
        subsectionHeader: 11,
        body: 10,
        small: 9,
        tableCell: 8,
        footnote: 7
    },
    spacing: {
        sectionGap: 20,
        paragraphGap: 10,
        itemGap: 6,
        tableCell: 4
    },
    colors: {
        primary: '#1e85e5',
        textDark: '#1a1a1a',
        textMuted: '#666666',
        textLight: '#888888',
        borderLight: '#e0e0e0',
        backgroundAlt: '#f8f9fa'
    }
};

const PDFGenerator = ({ proposal, customPercentage, currentUser, showLabel = false, onSessionExpired, onTokenRefresh, onQuoteSaved }) => {
    const apiUrl = getApiUrl();
    const authParams = { currentUser, onSessionExpired, onTokenRefresh };

    const [userLogo, setUserLogo] = useState("/nhclogo.png");
    const [userColor, setUserColor] = useState('');

    const today = new Date();
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    const formattedDate = today.toLocaleDateString('en-US', options);

    let nhcColor = "#1e85e5";

    useEffect(() => {
        getUserLogo();
    }, []);

    const getUserLogo = async () => {
        try {
            const response = await authFetch(
                `${apiUrl}/proposals/getuserdetails/${currentUser.id}`,
                { method: 'GET' },
                authParams
            );

            if (response.ok) {
                const data = await response.json();
                if (data.userDetails) {
                    setUserLogo(data.userDetails.customLogo);
                    setUserColor(data.userDetails.customColor);
                }
            }
            else {
                console.error("HTTP Error:", response.status, response.statusText);
                return;
            }
        } catch (err) {
            if (!(err instanceof SessionExpiredError)) {
                console.error("Failed to retrieve advisor logo");
            }
        }
    };

    const {
        headerBody,
        accountTotals,
        bencatTables,
        hasCoreHealth,
        generateCostEstimatesTable
    } = useQuoteCalculator(proposal, customPercentage);

    const generate = async () => {
        const logoImage = await getImageBase64(userLogo || '/nhclogo.png');
        const lastPage = await getImageBase64('/Benefit Proposal Generic.pdf');
        const fillColor = userColor ? userColor : nhcColor;

        let docDefinition = {
            pageMargins: [40, 50, 40, 50],
            header: function(currentPage, pageCount) {
                if (currentPage === 1) return null; // no header on cover page
                return [{
                    canvas: [{
                        type: 'rect', x: 0, y: 0, w: 595.28, h: 6, color: fillColor
                    }]
                }];
            },
            footer: function(currentPage, pageCount) {
                if (currentPage === 1) return null; // no footer on cover page
                return {
                    columns: [
                        { text: 'Benefit Plan Proposal', fontSize: 8, color: PDF_STYLES.colors.textLight, margin: [40, 0, 0, 0] },
                        { text: `Page ${currentPage} of ${pageCount}`, alignment: 'right', fontSize: 8, color: PDF_STYLES.colors.textLight, margin: [0, 0, 40, 0] }
                    ],
                    margin: [0, 15, 0, 0]
                };
            },
            content: [
                {
                    image: logoImage,
                    fit: [140, 45],
                    alignment: 'center',
                    margin: [0, 60, 0, 40]
                },
                {
                    text: "Benefit Plan Proposal",
                    fontSize: PDF_STYLES.fontSize.title,
                    font: "Merriweather",
                    alignment: "center",
                    color: PDF_STYLES.colors.textDark,
                    margin: [0, 0, 0, 20]
                },
                {
                    text: `Prepared for`,
                    margin: [0, 0, 0, 4],
                    font: "SourceSansPro",
                    alignment: "center",
                    fontSize: PDF_STYLES.fontSize.small,
                    color: PDF_STYLES.colors.textMuted,
                },
                {
                    text: proposal.clientName,
                    margin: [0, 0, 0, 4],
                    bold: true,
                    font: "Merriweather",
                    alignment: "center",
                    fontSize: PDF_STYLES.fontSize.subtitle,
                    color: fillColor,
                },
                {
                    text: formattedDate,
                    fontSize: PDF_STYLES.fontSize.small,
                    font: "SourceSansPro",
                    alignment: "center",
                    color: PDF_STYLES.colors.textMuted,
                    margin: [0, 0, 0, 40]
                },
                {
                    table: {
                        widths: ['*'],
                        body: [[
                            {
                                stack: [
                                    {
                                        text: "Your Advisor",
                                        font: "Merriweather",
                                        fontSize: PDF_STYLES.fontSize.subsectionHeader,
                                        bold: true,
                                        color: fillColor,
                                        alignment: 'center',
                                        margin: [0, 0, 0, 8]
                                    },
                                    {
                                        columns: [
                                            { width: '*', text: '' },
                                            {
                                                width: 'auto',
                                                table: {
                                                    body: [
                                                        [
                                                            { text: 'Name', fontSize: PDF_STYLES.fontSize.small, color: PDF_STYLES.colors.textMuted, alignment: 'right', margin: [0, 0, 10, 0] },
                                                            { text: proposal.advisorName, fontSize: PDF_STYLES.fontSize.small, color: PDF_STYLES.colors.textDark, bold: true }
                                                        ],
                                                        [
                                                            { text: 'Company', fontSize: PDF_STYLES.fontSize.small, color: PDF_STYLES.colors.textMuted, alignment: 'right', margin: [0, 0, 10, 0] },
                                                            { text: proposal.advisorCompany, fontSize: PDF_STYLES.fontSize.small, color: PDF_STYLES.colors.textDark, bold: true }
                                                        ],
                                                        [
                                                            { text: 'Email', fontSize: PDF_STYLES.fontSize.small, color: PDF_STYLES.colors.textMuted, alignment: 'right', margin: [0, 0, 10, 0] },
                                                            { text: proposal.advisorEmail, fontSize: PDF_STYLES.fontSize.small, color: PDF_STYLES.colors.textDark, bold: true }
                                                        ],
                                                    ]
                                                },
                                                layout: { hLineWidth: () => 0, vLineWidth: () => 0, paddingTop: () => 2, paddingBottom: () => 2 }
                                            },
                                            { width: '*', text: '' }
                                        ],
                                        margin: [0, 0, 0, 12]
                                    },
                                    {
                                        canvas: [{ type: 'line', x1: 60, y1: 0, x2: 350, y2: 0, lineWidth: 0.5, lineColor: PDF_STYLES.colors.borderLight }],
                                        margin: [0, 0, 0, 12]
                                    },
                                    {
                                        text: "Company Details",
                                        font: "Merriweather",
                                        fontSize: PDF_STYLES.fontSize.subsectionHeader,
                                        bold: true,
                                        color: fillColor,
                                        alignment: 'center',
                                        margin: [0, 0, 0, 8]
                                    },
                                    {
                                        columns: [
                                            { width: '*', text: '' },
                                            {
                                                width: 'auto',
                                                table: {
                                                    body: headerBody.map(row => {
                                                        const label = typeof row[0] === 'object' ? row[0].text : row[0];
                                                        const cleanLabel = label.endsWith(':') ? label.slice(0, -1) : label;
                                                        return [
                                                            { text: cleanLabel, fontSize: PDF_STYLES.fontSize.small, color: PDF_STYLES.colors.textMuted, alignment: 'right', margin: [0, 0, 10, 0] },
                                                            { text: typeof row[1] === 'object' ? row[1].text : row[1], fontSize: PDF_STYLES.fontSize.small, color: PDF_STYLES.colors.textDark, bold: true }
                                                        ];
                                                    })
                                                },
                                                layout: { hLineWidth: () => 0, vLineWidth: () => 0, paddingTop: () => 2, paddingBottom: () => 2 }
                                            },
                                            { width: '*', text: '' }
                                        ]
                                    }
                                ],
                                fillColor: PDF_STYLES.colors.backgroundAlt,
                                margin: [16, 12, 16, 12]
                            }
                        ]]
                    },
                    layout: {
                        hLineWidth: () => 0.5,
                        vLineWidth: () => 0.5,
                        hLineColor: () => PDF_STYLES.colors.borderLight,
                        vLineColor: () => PDF_STYLES.colors.borderLight,
                    },
                    margin: [60, 0, 60, 0]
                },
                ...Object.keys(bencatTables).map(accountType => {
                    const estimates = generateCostEstimatesTable(accountType);

                    return [
                        {
                            pageBreak: 'before',
                            text: `Your ${accountTypeHeaders[accountType]}`,
                            color: fillColor,
                            bold: true,
                            font: "Merriweather",
                            fontSize: PDF_STYLES.fontSize.subtitle,
                            margin: [0, 0, 0, 4]
                        },
                        {
                            canvas: [{ type: 'line', x1: 0, y1: 0, x2: 515, y2: 0, lineWidth: 2, lineColor: fillColor }],
                            margin: [0, 0, 0, 20]
                        },
                        {
                            table: {
                                widths: ['35%', '21.67%', '21.67%', '21.67%'],
                                body: [[
                                    {
                                        stack: [
                                            { text: 'Plan Budget', fontSize: PDF_STYLES.fontSize.tableCell, color: PDF_STYLES.colors.textMuted, margin: [0, 0, 0, 2] },
                                            { text: `$${estimates.planAccountBudget[accountType].toLocaleString()}`, fontSize: PDF_STYLES.fontSize.sectionHeader, bold: true, color: fillColor }
                                        ],
                                        border: [false, false, true, false],
                                        borderColor: [null, null, PDF_STYLES.colors.borderLight, null],
                                        margin: [0, 0, 24, 0]
                                    },
                                    {
                                        stack: [
                                            { text: 'Single', fontSize: PDF_STYLES.fontSize.tableCell, color: PDF_STYLES.colors.textMuted, margin: [0, 0, 0, 2] },
                                            { text: `${accountTotals[accountType].totalSingle || 0}`, fontSize: PDF_STYLES.fontSize.sectionHeader, bold: true }
                                        ],
                                        border: [false, false, false, false],
                                        margin: [24, 0, 24, 0]
                                    },
                                    {
                                        stack: [
                                            { text: 'Couple', fontSize: PDF_STYLES.fontSize.tableCell, color: PDF_STYLES.colors.textMuted, margin: [0, 0, 0, 2] },
                                            { text: `${accountTotals[accountType].totalCouple || 0}`, fontSize: PDF_STYLES.fontSize.sectionHeader, bold: true }
                                        ],
                                        border: [false, false, false, false],
                                        margin: [24, 0, 24, 0]
                                    },
                                    {
                                        stack: [
                                            { text: 'Family', fontSize: PDF_STYLES.fontSize.tableCell, color: PDF_STYLES.colors.textMuted, margin: [0, 0, 0, 2] },
                                            { text: `${accountTotals[accountType].totalFamily || 0}`, fontSize: PDF_STYLES.fontSize.sectionHeader, bold: true }
                                        ],
                                        border: [false, false, false, false],
                                        margin: [24, 0, 0, 0]
                                    }
                                ]]
                            },
                            layout: { defaultBorder: false, paddingLeft: () => 0, paddingRight: () => 0, paddingTop: () => 0, paddingBottom: () => 0 },
                            margin: [0, 0, 0, 24]
                        },
                        {
                            table: {
                                widths: CH_TYPES.includes(accountType) ? ['20%', '20%', '20%', '20%', '20%'] : ['35%', '21.67%', '21.67%', '21.67%'],
                                body: bencatTables[accountType].map((row, rowIdx) =>
                                    row.map((cell, cellIdx) => ({
                                        text: typeof cell === 'object' ? cell.text : cell,
                                        fillColor: rowIdx === 0 ? fillColor : (rowIdx % 2 === 1 ? '#f8f9fa' : 'white'),
                                        color: rowIdx === 0 ? 'white' : PDF_STYLES.colors.textDark,
                                        bold: rowIdx === 0 || cellIdx === 0,
                                        fontSize: PDF_STYLES.fontSize.small,
                                        alignment: cellIdx === 0 ? 'left' : 'center',
                                    }))
                                )
                            },
                            layout: {
                                paddingLeft: () => 10,
                                paddingRight: () => 10,
                                paddingTop: () => 8,
                                paddingBottom: () => 8,
                                hLineWidth: (i, node) => i === 1 ? 1 : 0.5,
                                hLineColor: (i) => i === 1 ? fillColor : PDF_STYLES.colors.borderLight,
                                vLineWidth: () => 0,
                            },
                            margin: [0, 0, 0, 24]
                        },
                        {
                            text: [
                                { text: 'Coverage Summary', font: "Merriweather", fontSize: PDF_STYLES.fontSize.subsectionHeader, bold: true },
                                { text: '  (Full list available upon request)', fontSize: PDF_STYLES.fontSize.footnote, italics: true, color: PDF_STYLES.colors.textMuted }
                            ],
                            color: fillColor,
                            margin: [0, 0, 0, 12]
                        },
                        {
                            columns: accountTypeCoverageSummaries[accountType].map(item => ({
                                stack: [
                                    { canvas: [{ type: 'ellipse', x: 3, y: 4, r1: 3, r2: 3, color: fillColor }] },
                                    { text: item, fontSize: PDF_STYLES.fontSize.small, color: PDF_STYLES.colors.textDark, margin: [12, -10, 0, 8] }
                                ],
                                width: 'auto'
                            })).slice(0, 4),
                            columnGap: 20,
                            margin: [0, 0, 0, 4]
                        },
                        accountTypeCoverageSummaries[accountType].length > 4 ? {
                            columns: accountTypeCoverageSummaries[accountType].slice(4).map(item => ({
                                stack: [
                                    { canvas: [{ type: 'ellipse', x: 3, y: 4, r1: 3, r2: 3, color: fillColor }] },
                                    { text: item, fontSize: PDF_STYLES.fontSize.small, color: PDF_STYLES.colors.textDark, margin: [12, -10, 0, 8] }
                                ],
                                width: 'auto'
                            })),
                            columnGap: 20,
                            margin: [0, 0, 0, 24]
                        } : { text: '', margin: [0, 0, 0, 20] },
                        {
                            text: `Projected Cost for ${accountTypeHeaders[accountType]}`,
                            color: fillColor,
                            bold: true,
                            font: "Merriweather",
                            fontSize: PDF_STYLES.fontSize.subsectionHeader,
                            margin: [0, 0, 0, 12]
                        },
                        {
                            text: `While the maximum cost for your plan is $${estimates.planAccountBudget[accountType].toLocaleString()}/year, the actual cost varies depending on employee usage of the ${getDescription(accountType)}. Below is the table showing your costs based on different percentages of ${getDescription(accountType)} claimed.`,
                            fontSize: PDF_STYLES.fontSize.small,
                            color: PDF_STYLES.colors.textMuted,
                            lineHeight: 1.5,
                            margin: [0, 0, 0, 20]
                        },
                        {
                            text: `${getDescription(accountType)} Cost Estimate`,
                            font: "Merriweather",
                            bold: true,
                            fontSize: PDF_STYLES.fontSize.subsectionHeader,
                            alignment: "center",
                            color: fillColor,
                            margin: [0, 0, 0, 16]
                        },
                        {
                            table: {
                                widths: ["28%", "*", "*", "*", "*", "*"],
                                body: estimates.rows.map((row, rowIdx) => {
                                    const isLastRow = rowIdx === estimates.rows.length - 1;
                                    return row.map((cell, cellIdx) => ({
                                        text: typeof cell === 'object' ? cell.text : cell,
                                        fillColor: rowIdx === 0 ? fillColor : isLastRow ? '#f0f7ff' : (rowIdx % 2 === 1 ? '#f8f9fa' : 'white'),
                                        color: rowIdx === 0 ? 'white' : isLastRow ? fillColor : PDF_STYLES.colors.textDark,
                                        bold: rowIdx === 0 || cellIdx === 0 || isLastRow,
                                        fontSize: PDF_STYLES.fontSize.small,
                                        alignment: cellIdx === 0 ? 'left' : 'right',
                                    }));
                                })
                            },
                            layout: {
                                paddingLeft: () => 10,
                                paddingRight: () => 10,
                                paddingTop: () => 8,
                                paddingBottom: () => 8,
                                hLineWidth: (i, node) => i === 1 || i === node.table.body.length - 1 ? 1 : 0.5,
                                hLineColor: (i, node) => i === 1 || i === node.table.body.length - 1 ? fillColor : PDF_STYLES.colors.borderLight,
                                vLineWidth: () => 0,
                            },
                        },
                        {
                            text: [
                                ...(proposal.province === "ON" ?
                                    (CH_TYPES.includes(accountType)) ?
                                        [
                                            { text: '* For Ontario, CoreHealth Insurance Premium includes an 8% RST.\n' },
                                            { text: `** ${provincesTaxRates[proposal.province] * 100 || 5}% provincial GST/HST rate.\n` },
                                            { text: '*** Additional ON tax includes 8% RST & 2% premium tax on the claim amount, 13% HST & 2% premium tax on the admin fee.\n' },
                                        ]
                                        :
                                        [
                                            { text: `* ${provincesTaxRates[proposal.province] * 100 || 5}% provincial GST/HST rate.\n` },
                                            { text: '** Additional ON tax includes 8% RST & 2% premium tax on the claim amount, 13% HST & 2% premium tax on the admin fee.\n' },
                                        ]
                                    : []),
                                ...(proposal.province === "ON" ? [
                                    { text: '\nNote: Calculation includes GST/HST for total dollars spent regardless of HSA or LSA allocation. Provincial taxes not applicable on any LSA reimbursements when the plan goes live. LSA claim amount is exempt from RST and will be reimbursed.\n' },
                                ] : [
                                    { text: `* ${provincesTaxRates[proposal.province] * 100 || 5}% provincial GST/HST rate.\n` }
                                ])
                            ],
                            fontSize: PDF_STYLES.fontSize.footnote,
                            color: PDF_STYLES.colors.textMuted,
                            lineHeight: 1.3,
                            margin: [0, 10, 0, 0]
                        }
                    ]
                }).flat(),
                ...(hasCoreHealth
                    ? [
                        {
                            pageBreak: "before",
                            text: "Group Insurance Coverage",
                            alignment: "center",
                            font: "Merriweather",
                            bold: true,
                            fontSize: PDF_STYLES.fontSize.subtitle,
                            color: PDF_STYLES.colors.textDark,
                            margin: [0, 20, 0, 20],
                        },
                        {
                            table: {
                                widths: ['40%', '30%', '30%'],
                                body: generateGroupInsuranceTable().map((row, rowIdx) =>
                                    row.map((cell, cellIdx) => ({
                                        text: typeof cell === 'object' ? cell.text : cell,
                                        fillColor: rowIdx === 0 ? fillColor : (rowIdx % 2 === 0 ? '#fafafa' : 'white'),
                                        color: rowIdx === 0 ? 'white' : PDF_STYLES.colors.textDark,
                                        bold: rowIdx === 0 || cellIdx === 0,
                                        fontSize: PDF_STYLES.fontSize.small,
                                        alignment: cellIdx === 0 ? 'left' : 'center',
                                    }))
                                ),
                            },
                            layout: {
                                paddingLeft: () => PDF_STYLES.spacing.tableCell,
                                paddingRight: () => PDF_STYLES.spacing.tableCell,
                                paddingTop: () => 8,
                                paddingBottom: () => 8,
                                hLineWidth: (i, node) => (i === 0 || i === node.table.body.length) ? 0 : 0.5,
                                hLineColor: () => PDF_STYLES.colors.borderLight,
                                vLineWidth: () => 0,
                            }
                        },
                    ]
                    : []
                ),
            ],
            defaultStyle: {
                font: 'SourceSansPro',
                fontSize: PDF_STYLES.fontSize.body,
                color: PDF_STYLES.colors.textDark,
                lineHeight: 1.3
            },
            styles: {
                header: {
                    font: 'Merriweather',
                    fontSize: PDF_STYLES.fontSize.sectionHeader,
                    bold: true,
                    color: PDF_STYLES.colors.textDark
                },
                subheader: {
                    font: 'SourceSansPro',
                    fontSize: PDF_STYLES.fontSize.body,
                    color: PDF_STYLES.colors.textMuted
                }
            }
        };

        // pdfMake.createPdf(docDefinition).download(proposal.clientName);
        const pdfMakeBlob = await new Promise((resolve) => {
            pdfMake.createPdf(docDefinition).getBlob(resolve);
        });
        const pdfMakeBuffer = await pdfMakeBlob.arrayBuffer();

        const lastPageResponse = await fetch('/files/Benefit Proposal Generic.pdf');
        const lastPageBuffer = await lastPageResponse.arrayBuffer();

        const generatedPdf = await PDFDocument.load(pdfMakeBuffer);
        const lastPagePdf = await PDFDocument.load(lastPageBuffer);

        const mergedPdf = await PDFDocument.create();

        // Copy pages from generated PDF
        const generatedPages = await mergedPdf.copyPages(generatedPdf, generatedPdf.getPageIndices());
        generatedPages.forEach(page => mergedPdf.addPage(page));

        // Copy pages from last page PDF
        const lastPagePages = await mergedPdf.copyPages(lastPagePdf, lastPagePdf.getPageIndices());
        lastPagePages.forEach(page => mergedPdf.addPage(page));

        // ✅ Normalize all pages to the same size (use first page as reference)
        const targetSize = generatedPdf.getPage(0).getSize(); // e.g. { width: 595, height: 842 } for A4

        mergedPdf.getPages().forEach((page) => {
            const { width, height } = page.getSize();

            if (width !== targetSize.width || height !== targetSize.height) {
                // Scale content to fit target size
                const scaleX = targetSize.width / width;
                const scaleY = targetSize.height / height;

                page.setSize(targetSize.width, targetSize.height);
                page.scaleContent(scaleX, scaleY);
            }
        });

        const mergedPdfBytes = await mergedPdf.save();
        const blob = new Blob([mergedPdfBytes], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${proposal.clientName}.pdf`;
        link.click();
        URL.revokeObjectURL(url);

        if (onQuoteSaved) {
            await onQuoteSaved();
        }
    }

    return (
        <button
            type="button"
            className={showLabel
                ? "inline-flex items-center justify-center gap-2 h-9 px-4 text-sm font-medium text-white bg-primary rounded-xl hover:bg-primary-dark transition-colors"
                : "p-1.5 text-primary border border-primary rounded-lg hover:bg-primary hover:!text-white transition-colors"
            }
            onClick={generate}
            title={showLabel ? undefined : "Download Quote"}
        >
            <Download className="w-4 h-4" />
            {showLabel && "Download Quote"}
        </button>
    );
}

export default PDFGenerator;
